import { type ReactNode } from 'react'
import { FormContainer } from '@/components/FormContainer'
import { PersonalInfoEditor } from '@/features/tax-profile/individual-profiles/components/PersonalInfoEditor'
import type { PersonalProfileEditorViewModel } from '@/features/tax-profile/individual-profiles/PersonalProfileEditorViewModel'
import { ContactInfoEditor } from '@/features/tax-profile/components/ContactInfoEditor'
import { TextFieldContainer } from '@/components/inputs/TextFieldContainer'
import { FormFieldSet, FormFieldSetLegend } from '@/components/Form'
import { HeadsUpBlock } from '@/components/HeadsUpBlock'
import { observer } from 'mobx-react-lite'
import { IsSkeletonContext } from '@/components/Skeleton'

export interface PersonalProfileEditorProps {
  children?: ReactNode
  formId?: string
  isDisabled?: boolean
  vm: PersonalProfileEditorViewModel
  isSkeleton?: boolean
}

/**
 * Form for editing a personal profile.
 */
export const PersonalProfileEditor = observer(function PersonalProfileEditor({
  formId,
  isDisabled,
  isSkeleton,
  vm,
}: PersonalProfileEditorProps) {
  const testId = 'personal-profile-editor'
  return (
    <IsSkeletonContext value={isSkeleton}>
      <FormContainer
        id={formId}
        onSubmit={vm.save}
        disabled={isDisabled || vm.save.pending}
        testId={testId}
      >
        {vm.isPrefilledUsingLatest && (
          <HeadsUpBlock title="Existing information found">
            It looks like we already have some of your personal information on
            record, so we've pre-filled it below.
          </HeadsUpBlock>
        )}

        <FormFieldSet>
          <FormFieldSetLegend>Personal information</FormFieldSetLegend>
          <PersonalInfoEditor
            vm={vm.personalInfoVm}
            privacyDialogTestId={`${testId}-privacy-dialog`}
            privacyDialogCloseButtonTestId={`${testId}-privacy-dialog-close-button`}
            testId={testId}
          />
          <TextFieldContainer
            model={vm}
            field="occupation"
            label="Occupation"
            testId={`${testId}-occupation-input`}
          />
        </FormFieldSet>

        <FormFieldSet>
          <FormFieldSetLegend>Contact information</FormFieldSetLegend>
          <ContactInfoEditor
            vm={vm.contactInfoVm}
            testId={`${testId}-contact-info`}
          />
        </FormFieldSet>
      </FormContainer>
    </IsSkeletonContext>
  )
})
